import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { ContactLanguage } from '~/api/clients/types';
import type { Product } from '~/api/products/types';

import { useProducts } from '~/api/products';
import { useUser } from '~/api/user';
import { createAppColumnHelper, Table } from '~/components';
import { ColumnIds } from '~/constants/table';
import { ProductStatusTag } from '~/features/products';
import { useIntl } from '~/hooks/useIntl';
import { useProductCategories } from '~/hooks/useProductCategories';
import { clsx } from '~/styles/utils';

import { getProductName } from '../../FormLines.utils';
import { mutedCellStyle, priceCellStyle, truncatedCellStyle } from './ProductPickerDialog.css';
import { ProductPickerMobileCell } from './ProductPickerMobileCell';
import { ProductPickerOptionsCell } from './ProductPickerOptionsCell';

const columnHelper = createAppColumnHelper<Product>();

export const useProductsTableColumns = (language: ContactLanguage) => {
  const filters = useProductsTableFilters();
  const { data: user } = useUser();

  const { t } = useTranslation(['lines', 'products']);
  const { formatCurrency } = useIntl();

  return useMemo(
    () =>
      columnHelper.columns([
        // Selector
        columnHelper.display({
          id: ColumnIds.selector,
          header: () => <Table.SelectAll />,
          cell: ({ row }) => <Table.SelectRow row={row} />,
          meta: {
            styles: { size: 'max-content' },
          },
        }),

        // Name
        columnHelper.accessor(({ name }) => `${name.nl ?? ''} ${name.fr ?? ''} ${name.en ?? ''}`, {
          id: ColumnIds.productsName,
          header: t('products:overview.columns.name'),
          cell: ({ row: { original: product } }) => (
            <div className={clsx(truncatedCellStyle, product.status === 'Rejected' && mutedCellStyle)}>
              {getProductName(product, language, user.lang)}
            </div>
          ),
          meta: {
            styles: { size: '2fr', minSize: 100 },
          },
        }),

        // Status
        columnHelper.accessor('status', {
          id: ColumnIds.productsStatus,
          header: t('products:overview.columns.status.title'),
          cell: ({ getValue }) => <ProductStatusTag status={getValue()} />,
          filterFn: 'equalsOneOf',
          sortFn: (a, b) => {
            const priority = { InReview: 3, Approved: 2, Rejected: 1 } as const;
            return priority[a.original.status] > priority[b.original.status] ? -1 : 1;
          },
          enableGlobalFilter: false,
          meta: {
            styles: { justify: 'center', minSize: 75, size: 'max-content' },
            filterOptions: filters.statuses,
          },
        }),

        // Activity type
        columnHelper.accessor(({ activityType }: Product) => t(`lines:creativeWork.options.${activityType}.label`), {
          id: ColumnIds.productsActivityType,
          header: t('products:overview.columns.creativeWork'),
          cell: ({ getValue }) => <div className={truncatedCellStyle}>{getValue()}</div>,
          filterFn: 'equalsOneOf',
          enableGlobalFilter: false,
          meta: {
            styles: { justify: 'center', minSize: 100, size: 'max-content' },
            filterOptions: filters.activityTypes,
          },
        }),

        // Price
        columnHelper.accessor(({ price }) => price ?? undefined, {
          id: ColumnIds.productsPrice,
          header: t('products:overview.columns.price'),
          cell: ({ getValue }) => {
            const price = getValue();

            return <div className={priceCellStyle}>{price ? formatCurrency(price) : '-'}</div>;
          },
          sortUndefined: 'last',
          meta: {
            styles: { justify: 'end', minSize: 100, size: 'max-content' },
          },
        }),

        // Categories
        columnHelper.accessor(({ productCategories }: Product) => (productCategories.length > 0 ? productCategories : undefined), {
          id: ColumnIds.productsCategories,
          header: t('products:overview.columns.productCategories'),
          cell: ({ row: { original: product } }) => <div className={truncatedCellStyle}>{product.productCategories.join(', ')}</div>,
          filterFn: 'arrIncludesSome',
          sortUndefined: 'last',
          meta: {
            styles: { minSize: 100, size: '1fr' },
            filterOptions: filters.categories,
          },
        }),

        // Options
        columnHelper.display({
          id: ColumnIds.options,
          cell: ({ row: { original: product } }) => <ProductPickerOptionsCell product={product} />,
          meta: {
            styles: { minSize: 40, size: 'max-content' },
          },
        }),

        // Mobile
        columnHelper.display({
          id: ColumnIds.productsMobile,
          cell: ({ row }) => <ProductPickerMobileCell language={language} row={row} />,
          meta: {
            mobile: true,
          },
        }),
      ]),
    [filters, formatCurrency, language, t, user.lang],
  );
};

const useProductsTableFilters = () => {
  const { data: products } = useProducts();
  const productCategories = useProductCategories();

  const { t } = useTranslation(['lines', 'products']);
  const { compareForSort } = useIntl();

  const statusOptions = useMemo(
    () =>
      (['Approved', 'InReview', 'Rejected'] satisfies Product['status'][]).map((status) => ({
        label: t(`products:overview.columns.status.options.${status}`),
        value: status,
      })),
    [t],
  );

  const activityTypeOptions = useMemo(
    () =>
      [...new Set(products.map(({ activityType }) => activityType))].sort(compareForSort).map((activityType) => ({
        label: t(`lines:creativeWork.options.${activityType}.label`),
        value: t(`lines:creativeWork.options.${activityType}.label`),
      })),
    [compareForSort, products, t],
  );

  const categoryOptions = useMemo(
    () =>
      productCategories.map((category) => ({
        label: category,
        value: category,
      })),
    [productCategories],
  );

  return useMemo(
    () => ({
      activityTypes: activityTypeOptions,
      categories: categoryOptions,
      statuses: statusOptions,
    }),
    [activityTypeOptions, categoryOptions, statusOptions],
  );
};
